/**
 * Product Card Component
 * Reusable card for displaying products in grids and carousels
 */

'use client';

import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { Rating, useTheme, ComponentSize } from '@template-project/afrodita-components';
import { useIntl } from '@/lib/intl';

export interface ProductCardProps {
  id: string;
  slug: string;
  name: string;
  image: string;
  price: number;
  compareAtPrice?: number;
  rating?: number;
  reviewCount?: number;
  /** Top badge (e.g., "Black Friday") */
  topBadge?: string;
  /** Bottom badge (e.g., "Selling Fast", "Best Seller") */
  bottomBadge?: string;
  bottomBadgeType?: 'selling-fast' | 'bestseller';
  /** @deprecated Use topBadge and bottomBadge instead */
  badge?: string;
  /** @deprecated Use bottomBadgeType instead */
  badgeType?: 'sale' | 'bestseller' | 'new' | 'selling-fast';
  currency?: string;
}

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-4px);
  }
  
  &:hover .product-image {
    transform: scale(1.03);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: ${({ theme }) => theme.radius.regular};
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background.secondary};
`;

interface ProductImageProps {
  $loaded: boolean;
}

const ProductImage = styled.img<ProductImageProps>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease, opacity 0.3s ease;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    opacity: 1;
  }
`;

// Top badge - "Black Friday" style
const TopBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.space.sm};
  left: ${({ theme }) => theme.space.sm};
  z-index: 2;
`;

const TopBadgeImage = styled.img`
  height: 22px;
  width: auto;
`;

// Bottom badge - "Selling Fast" or "Best Seller"
interface BottomBadgeProps {
  $type: 'selling-fast' | 'bestseller';
}

const BottomBadge = styled.div<BottomBadgeProps>`
  position: absolute;
  bottom: ${({ theme }) => theme.space.sm};
  left: ${({ theme }) => theme.space.sm};
  z-index: 2;
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  border-radius: ${({ theme }) => theme.radius.small};
  
  ${({ $type, theme }) => {
    switch ($type) {
      case 'bestseller':
        return `
          background-color: ${theme.colors.shades.white.color};
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.border.color};
        `;
      case 'selling-fast':
        return `
          background-color: ${theme.colors.semantic.attention.color};
          color: ${theme.colors.text.primary};
        `;
      default:
        return `
          background-color: ${theme.colors.shades.white.color};
          color: ${theme.colors.text.primary};
        `;
    }
  }}
`;

const SellingFastIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '4px' }}>
    <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
  </svg>
);

// Black Friday badge URL
const BLACK_FRIDAY_BADGE_URL = 'https://cdn.shapermint.com/assets/shapermint/images/transition/black-friday-extended/seal.svg';

const ProductInfo = styled.div`
  padding: ${({ theme }) => theme.space.md} 0;
`;

const ProductName = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: center;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm};
  margin-top: ${({ theme }) => theme.space.sm};
`;

const ComparePrice = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: line-through;
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: ${({ theme }) => theme.radius.full};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: help;
`;

const CurrentPrice = styled.span`
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PriceDollar = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
`;

const PriceMain = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[6]}px;
`;

const PriceCents = styled.sup`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
`;

const RatingContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.space.sm};
`;

function formatPriceParts(
  cents: number,
  currency: string,
  locale: string
): { symbol: string; dollars: string; cents: string } {
  const amount = cents / 100;
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const parts = formatter.formatToParts(amount);
  const symbol = parts.find((p) => p.type === 'currency')?.value || '$';
  const integerParts = parts
    .filter((p) => p.type === 'integer' || p.type === 'group')
    .map((p) => p.value)
    .join('');
  const fractionPart = parts.find((p) => p.type === 'fraction')?.value || '00';

  return { symbol, dollars: integerParts || '0', cents: fractionPart };
}

export function ProductCard({
  id,
  slug,
  name,
  image,
  price,
  compareAtPrice,
  rating,
  reviewCount,
  topBadge,
  bottomBadge,
  bottomBadgeType = 'bestseller',
}: ProductCardProps) {
  const theme = useTheme();
  const { countryConfig, localizedPath } = useIntl();
  const [imageLoaded, setImageLoaded] = useState(false);
  const currencyCode = countryConfig.currency.code;
  const locale = countryConfig.locale;
  const formattedPrice = formatPriceParts(price, currencyCode, locale);
  const formattedComparePrice = compareAtPrice
    ? formatPriceParts(compareAtPrice, currencyCode, locale)
    : null;
  
  return (
    <Card to={localizedPath(`/products/${slug || id}`)}>
      <ImageContainer theme={theme}>
        <ProductImage
          className="product-image"
          src={image}
          alt={name}
          loading="lazy"
          $loaded={imageLoaded}
          onLoad={() => setImageLoaded(true)}
        />
        {topBadge && (
          <TopBadge theme={theme}>
            <TopBadgeImage 
              src={BLACK_FRIDAY_BADGE_URL} 
              alt={topBadge}
              loading="lazy"
            />
          </TopBadge>
        )}
        {bottomBadge && (
          <BottomBadge theme={theme} $type={bottomBadgeType}>
            {bottomBadgeType === 'selling-fast' && <SellingFastIcon />}
            {bottomBadge}
          </BottomBadge>
        )}
      </ImageContainer>
      
      <ProductInfo theme={theme}>
        <ProductName theme={theme}>{name}</ProductName>
        
        <PriceContainer theme={theme}>
          {formattedComparePrice && (
            <>
              <ComparePrice theme={theme}>
                {countryConfig.currency.format}{formattedComparePrice.dollars}.{formattedComparePrice.cents}
              </ComparePrice>
              <InfoIcon theme={theme}>i</InfoIcon>
            </>
          )}
          <CurrentPrice theme={theme}>
            <PriceDollar theme={theme}>{countryConfig.currency.format}</PriceDollar>
            <PriceMain theme={theme}>{formattedPrice.dollars}</PriceMain>
            <PriceCents theme={theme}>{formattedPrice.cents}</PriceCents>
          </CurrentPrice>
        </PriceContainer>
        
        {rating !== undefined && reviewCount !== undefined && (
          <RatingContainer theme={theme}>
            <Rating 
              rating={rating} 
              reviews={reviewCount}
              size={ComponentSize.Small}
              wrapWithParenthesis
            />
          </RatingContainer>
        )}
      </ProductInfo>
    </Card>
  );
}
